import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import MobileLayout from "@/components/MobileLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, X, FileText, CheckCircle2, ArrowLeft, Plus, Clock, Files, Package } from "lucide-react";
import api, { API, SESSION_ID, formatApiError } from "@/lib/api";
import { countPages, randomKpCode } from "@/lib/files";
import DynamicConfigurator from "@/components/order/DynamicConfigurator";
import FileCard from "@/components/order/FileCard";
import ServiceSelector from "@/components/order/ServiceSelector";
import {
  consumeStashedSharedFiles,
  getShareDebugLog,
  getShareErrors,
  onSharedFiles,
  shareDbg,
  clearSharedFiles,
} from "@/lib/shareReceiver";

const optionRow = (label, name, opts, value, setValue) => (
  <div>
    <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">{label}</Label>
    <RadioGroup
      value={value} onValueChange={setValue}
      className="mt-2 grid grid-cols-2 gap-2"
    >
      {opts.map((o) => (
        <label
          key={o.value}
          data-testid={`opt-${name}-${o.value}`}
          className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-base font-bold ${
            value === o.value
              ? "border-[#4d7c0f] bg-[#4d7c0f]/5 text-[#4d7c0f]"
              : "border-slate-200 text-slate-700"
          }`}
        >
          <RadioGroupItem value={o.value} className="sr-only" />
          {o.label}
        </label>
      ))}
    </RadioGroup>
  </div>
);



function mergeDefaultConfiguration(current, defaults) {
  if (!defaults) return current;

  return {
    ...current,
    ...defaults,
    finishes: Array.isArray(defaults.finishes)
      ? defaults.finishes
      : (current.finishes || []),
  };
}

function rememberGuestName(name) {
  // No guardamos nombres que parezcan códigos KP-XXXX: son placeholders
  // generados, no nombres reales del usuario. Si se guardan, en futuras
  // sesiones aparecerían como "A nombre de" causando incoherencias con el
  // KP que reserva el backend.
  if (name && name.trim() && !/^KP-\d{3,5}$/i.test(name.trim())) {
    localStorage.setItem("kapei_guest_name", name.trim());
  }
}
function getGuestName() {
  const v = localStorage.getItem("kapei_guest_name") || "";
  // Limpieza defensiva: si en sesiones anteriores se guardó un KP-XXXX,
  // lo descartamos.
  if (/^KP-\d{3,5}$/i.test(v)) {
    localStorage.removeItem("kapei_guest_name");
    return "";
  }
  return v;
}

// Debug flag para mostrar el panel técnico de Share Intent. Solo se activa
// si el usuario hace explícitamente `localStorage.DEBUG_SHARE = "1"` desde
// chrome://inspect. Por defecto, los clientes nunca lo ven.
function isShareDebugEnabled() {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem("DEBUG_SHARE") === "1";
  } catch (_) {
    return false;
  }
}

export default function SendPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Cuando el usuario entra por el botón Compartir nativo (?fromShare=1) le
  // mostramos directamente el formulario completo (mismo flujo que "Configurar
  // impresión") con los archivos preadjuntos. En el flujo manual, "quick"
  // sigue siendo simple.
  const fromShare = params.get("fromShare") === "1";
  const initialMode = fromShare
    ? "configured"
    : params.get("mode") === "quick"
    ? "quick"
    : "configured";
  const [mode, setMode] = useState(initialMode);
  // entries: { file: File, pages: number|null }
  const [entries, setEntries] = useState([]);
  // Code hint that we'll show pre-filled to guests so they know the code they'll
  // get. We pass it to the backend, which uses it as the actual code if it's not taken.
  const [codeHint] = useState(() => randomKpCode());
  const initialName = user?.name || getGuestName() || (user ? "" : codeHint);
  const [nameOverride, setNameOverride] = useState(initialName);
  const [note, setNote] = useState("");
  const [opts, setOpts] = useState({ copies: 1, color: "bw", sides: "double", size: "A4", paper: "normal" });
  const [sizeOther, setSizeOther] = useState("");
  const [paperOther, setPaperOther] = useState("");
  const [autoSidesSet, setAutoSidesSet] = useState(false);
  const [pickup, setPickup] = useState(false);
  const [pickupAt, setPickupAt] = useState("");
  const [activePrize, setActivePrize] = useState(null);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    api.get("/admin/service-catalog")
      .then(({ data }) => setServiceCatalog(data || []))
      .catch(() => setServiceCatalog([]));
  }, []);

  useEffect(() => {
    if (!user) {
      setActivePrize(null);
      return;
    }

    api.get("/promotions/mine")
      .then(({ data }) => {
        const prize = (data || []).find((promo) =>
          promo.source === "raffle" &&
          promo.claim_status === "claimed" &&
          promo.can_use === true
        );
        setActivePrize(prize || null);
      })
      .catch(() => setActivePrize(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const deactivateActivePrize = async () => {
    if (!activePrize?.code) return;
    try {
      await api.post(`/promotions/${activePrize.code}/dont-use-next-order`);
      setActivePrize(null);
    } catch {
      // No molestamos al cliente si falla puntualmente
    }
  };

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const isBusinessDay = (date) => {
    const day = date.getDay();
    return day >= 1 && day <= 5;
  };

  const buildTimeSlots = () => {
    const ranges = [
      ["12:00", "13:30"],
      ["16:15", "20:00"],
    ];

    const slots = [];

    for (const [start, end] of ranges) {
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      let total = sh * 60 + sm;
      const endTotal = eh * 60 + em;

      while (total <= endTotal) {
        const h = String(Math.floor(total / 60)).padStart(2, "0");
        const m = String(total % 60).padStart(2, "0");
        slots.push(`${h}:${m}`);
        total += 15;
      }
    }

    return slots;
  };

  const pickupDate = pickupAt ? pickupAt.slice(0, 10) : "";
  const pickupTime = pickupAt ? pickupAt.slice(11, 16) : "";
  const now = new Date();
  const todayDate = formatLocalDate(now);
  const allTimeSlots = buildTimeSlots();

  const isFutureSlot = (dateValue, timeValue) => {
    if (dateValue !== todayDate) return true;
    const [h, m] = timeValue.split(":").map(Number);
    const slot = new Date();
    slot.setHours(h, m, 0, 0);
    return slot > now;
  };

  const getAvailableSlotsForDate = (dateValue) =>
    allTimeSlots.filter((slot) => isFutureSlot(dateValue, slot));

  const buildPickupDays = () => {
    const days = [];
    const d = new Date();

    while (days.length < 30) {
      if (isBusinessDay(d)) {
        const value = formatLocalDate(d);
        const slots = getAvailableSlotsForDate(value);

        if (slots.length > 0) {
          const diffDays = Math.round((new Date(`${value}T12:00:00`) - new Date(`${todayDate}T12:00:00`)) / 86400000);
          const weekday = d.toLocaleDateString("es-ES", { weekday: "short" }).replace(".", "");
          const dayMonth = d.toLocaleDateString("es-ES", { day: "numeric", month: "short" }).replace(".", "");
          const label = diffDays === 0 ? "Hoy" : diffDays === 1 ? "Mañana" : diffDays === 2 ? "Pasado mañana" : `${weekday} ${dayMonth}`;
          days.push({ value, label });
        }
      }

      d.setDate(d.getDate() + 1);
    }

    return days;
  };

  const pickupDays = buildPickupDays();
  const visiblePickupDays = pickupDays.slice(0, 6);
  const extraPickupDays = pickupDays.slice(6);
  const pickupTimeSlots = pickupDate ? getAvailableSlotsForDate(pickupDate) : [];

  const configuratorServices = useMemo(() => {
    return (serviceCatalog || [])
      .filter((item) =>
        item.category === "configuradores" &&
        item.visible_client !== false &&
        item.status !== "hidden"
      )
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [serviceCatalog]);

  useEffect(() => {
    if (!selectedService && configuratorServices.length > 0) {
      setSelectedService(configuratorServices[0]);
    }
  }, [configuratorServices, selectedService]);


  const updatePickupDateTime = (date, time) => {
    const d = date || pickupDays[0]?.value || todayDate;
    const slots = getAvailableSlotsForDate(d);
    const t = slots.includes(time) ? time : (slots[0] || "");
    setPickupAt(t ? `${d}T${t}` : "");
  };

  const ensurePickupDateTime = () => {
    if (!pickupAt && pickupDays.length > 0) {
      updatePickupDateTime(pickupDays[0].value, "");
    }
  };
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");
  const [openOrder, setOpenOrder] = useState(null);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [appendMode, setAppendMode] = useState(null);
  const inputRef = useRef(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugLog, setDebugLog] = useState([]);
  const [shareErrors, setShareErrors] = useState([]);

  useEffect(() => {
    if (user?.name && nameOverride === codeHint) setNameOverride(user.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name]);

  // Detect existing open order on mount + handle ?append=<order_id>
  // and files pre-attached via OS share intent (location.state.sharedFiles).
  useEffect(() => {
    const requestedAppend = params.get("append");
    api.get("/orders/open").then(({ data }) => {
      if (data?.open) {
        setOpenOrder(data.order);
        if (requestedAppend && data.order?.id === requestedAppend) {
          setAppendMode(requestedAppend);
        }
      }
    }).catch(() => {});

    // If the user got here through the OS Share menu, the share receiver has
    // already materialised the files as JS File objects and put them in state.
    // Doble red de seguridad: leemos location.state.sharedFiles *y* el stash
    // global (window.__kapeiSharedFiles) por si el state se perdió en cold
    // start (cuando se recarga la app justo al abrir el share).
    const shared = location.state?.sharedFiles;
    const stashed = consumeStashedSharedFiles();
    const incoming = (Array.isArray(shared) && shared.length > 0)
      ? shared
      : (Array.isArray(stashed) && stashed.length > 0 ? stashed : null);
    if (incoming) {
      shareDbg(
        `✓ RECIBIDO en mount: ${incoming.length} archivos (origen=${shared ? "state" : "stash"})`
      );
      onPick(incoming);
      // wipe state so a back-navigation doesn't re-add them
      navigate(location.pathname + location.search, { replace: true, state: null });
    } else if (params.get("fromShare") === "1") {
      shareDbg("⚠️ mount con ?fromShare=1 pero sin archivos en state ni stash todavía");
    }

    // Tercera red de seguridad: si la pantalla /send se monta ANTES de que
    // shareReceiver termine de convertir los ficheros (cold-start lento),
    // nos suscribimos para recibir los archivos cuando lleguen.
    const unsubscribe = onSharedFiles((files) => {
      if (Array.isArray(files) && files.length > 0) {
        shareDbg(`✓ RECIBIDO via subscriber: ${files.length} archivos`);
        onPick(files);
      }
      setShareErrors(getShareErrors());
    });

    // Recupera errores que shareReceiver haya podido dejar para diagnóstico.
    setShareErrors(getShareErrors());

    return () => {
      try { unsubscribe(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = useMemo(
    () => entries.reduce((acc, e) => acc + (typeof e.pages === "number" ? e.pages : 0), 0),
    [entries]
  );
  const hasUnknownPageCount = useMemo(
    () => entries.some((e) => e.pages === null),
    [entries]
  );
  const totalSize = useMemo(
    () => entries.reduce((acc, e) => acc + e.file.size, 0),
    [entries]
  );

  // Auto-default sides removed: KAPEI prefers a fixed default of 2 caras (double).
  // Users can still toggle to "single" manually.

  const onPick = async (list) => {
    const candidates = Array.from(list || []);
    // Dedupe contra los que ya están en entries (clave: name + size + type
    // + lastModified). Previene que Share Intent + subscriber añadan dos
    // veces los mismos archivos en cold-start/warm-start.
    const fileKey = (f) =>
      `${f.name}|${f.size}|${f.type || ""}|${f.lastModified || 0}`;
    setEntries((prev) => {
      const seen = new Set(prev.map((e) => fileKey(e.file)));
      const fresh = [];
      for (const f of candidates) {
        const k = fileKey(f);
        if (!seen.has(k)) {
          seen.add(k);
          fresh.push({ file: f, pages: undefined });
        }
      }
      return [...prev, ...fresh];
    });
    // Cuenta páginas solo para los que de verdad se añadieron. Como
    // setEntries es asíncrono, recalculamos qué claves son nuevas usando
    // el snapshot anterior visible aquí (entries) — best-effort para no
    // procesar duplicados otra vez.
    const seenNow = new Set(entries.map((e) => fileKey(e.file)));
    for (const f of candidates) {
      if (seenNow.has(fileKey(f))) continue;
      seenNow.add(fileKey(f));
      const pages = await countPages(f);
      setEntries((prev) =>
        prev.map((e) => (e.file === f ? { ...e, pages: pages == null ? null : pages } : e))
      );
    }
  };

  const removeFile = (idx) => setEntries((prev) => prev.filter((_, i) => i !== idx));

  const setSidesManual = (v) => {
    setOpts({ ...opts, sides: v });
  };

  const tryStartUpload = () => {
    setErr("");
    if (entries.length === 0) { setErr("Añade al menos un archivo."); return; }
    if (mode === "configured" && opts.size === "Otro" && !sizeOther.trim()) {
      setErr("Indica el tamaño que necesitas."); return;
    }
    if (mode === "configured" && opts.paper === "otro" && !paperOther.trim()) {
      setErr("Indica el tipo de papel que necesitas."); return;
    }
    if (openOrder && !appendMode) {
      setShowOpenDialog(true);
      return;
    }
    submit();
  };

  const submit = async (forceNew = false, explicitAppendId = null) => {
    setShowOpenDialog(false);
    setUploading(true);
    setProgress(0);
    rememberGuestName(nameOverride);
    try {
      const fd = new FormData();
      entries.forEach((e) => fd.append("files", e.file, e.file.name));

      const t = localStorage.getItem("kapei_token");
      // IMPORTANT: do NOT manually set Content-Type for multipart - the browser/axios
      // sets it with the proper boundary parameter. Setting it without boundary
      // would break the upload.
      const headers = { "X-Session-Id": SESSION_ID };
      if (t) headers.Authorization = `Bearer ${t}`;

      const targetAppendId = explicitAppendId || appendMode;
      let url, data;
      const appendCurrentOptions = () => {
        fd.append("mode", mode);
        if (note) fd.append("note", note);
        if (!hasUnknownPageCount && entries.length > 0) {
          fd.append("page_count", String(totalPages));
        }
        if (mode === "configured") {
          if (selectedService?.slug) fd.append("service_slug", selectedService.slug);
          fd.append("copies", String(opts.copies));
          fd.append("color", opts.color);
          fd.append("sides", opts.sides);
          fd.append("size", opts.size);
          fd.append("paper", opts.paper);
          if (Array.isArray(opts.finishes) && opts.finishes.length > 0) fd.append("finishes", JSON.stringify(opts.finishes));
          if (opts.size === "Otro" && sizeOther.trim()) fd.append("size_other", sizeOther.trim());
          if (opts.paper === "otro" && paperOther.trim()) fd.append("paper_other", paperOther.trim());
        }
        if (pickup) {
          fd.append("pickup_ready", "true");
          if (pickupAt) fd.append("pickup_datetime", pickupAt);
        }
      };

      if (targetAppendId && !forceNew) {
        appendCurrentOptions();
        url = `${API}/orders/${targetAppendId}/files`;
        const resp = await axios.post(url, fd, {
          withCredentials: true, headers,
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); },
        });
        data = resp.data;
        data.appended = true;
      } else {
        fd.append("mode", mode);
        // Solo enviamos name_override si:
        //   - el usuario lo cambió respecto al placeholder por defecto (codeHint), Y
        //   - el valor no parece un código KP-XXXX (que podría ser residuo de
        //     una sesión anterior si el guest cerró sin escribir).
        // Sin esto, el backend usa el KP que de verdad reserve como
        // display_name → coherencia entre el KP grande y el "A nombre de:".
        const trimmedName = (nameOverride || "").trim();
        const isKpLooking = /^KP-\d{3,5}$/i.test(trimmedName);
        if (trimmedName && trimmedName !== codeHint && !isKpLooking) {
          fd.append("name_override", trimmedName);
        }
        if (note) fd.append("note", note);
        if (!user) fd.append("code_hint", codeHint);
        if (!hasUnknownPageCount && entries.length > 0) {
          fd.append("page_count", String(totalPages));
        }
        if (mode === "configured") {
          if (selectedService?.slug) fd.append("service_slug", selectedService.slug);
          fd.append("copies", String(opts.copies));
          fd.append("color", opts.color);
          fd.append("sides", opts.sides);
          fd.append("size", opts.size);
          fd.append("paper", opts.paper);
          if (Array.isArray(opts.finishes) && opts.finishes.length > 0) fd.append("finishes", JSON.stringify(opts.finishes));
          if (opts.size === "Otro" && sizeOther.trim()) fd.append("size_other", sizeOther.trim());
          if (opts.paper === "otro" && paperOther.trim()) fd.append("paper_other", paperOther.trim());
        }
        if (pickup) {
          fd.append("pickup_ready", "true");
          if (pickupAt) fd.append("pickup_datetime", pickupAt);
        }
        const resp = await axios.post(`${API}/orders`, fd, {
          withCredentials: true, headers,
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); },
        });
        data = resp.data;
      }

      if (forceNew && (appendMode || explicitAppendId)) {
        data.created_new_after_closed = true;
      }
      // 🧹 Tras un envío exitoso por Share Intent, limpia el stash global y
      // los archivos del formulario. Sin esto, un evento tardío
      // (resume/appUrlOpen) podría reinyectar los mismos archivos en la
      // siguiente sesión de envío, o al volver atrás reaparecería el form
      // listo para reenviar el mismo pedido.
      if (fromShare) {
        clearSharedFiles();
        shareDbg("✓ envío correcto: stash limpiado (firma del intent conservada)");
      }
      // Limpia el formulario en cualquier caso (Share Intent o manual) para
      // que volver atrás no muestre los archivos listos para reenviar.
      setEntries([]);
      setNote("");
      setProgress(0);
      setSuccess(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && (appendMode || explicitAppendId)) {
        // El pedido anterior ya fue abierto por KAPEI. Para no obligar al cliente
        // a repetir el envío, creamos automáticamente un pedido nuevo con los mismos archivos.
        setOpenOrder(null);
        setAppendMode(null);
        try {
          const retryData = await submit(true);
          return retryData;
        } catch (_) {
          setErr("KAPEI ya estaba revisando el pedido anterior. No se pudo crear el nuevo pedido automáticamente. Inténtalo de nuevo.");
        }
      } else if (e.response?.status === 409) {
        setOpenOrder(null);
        setAppendMode(null);
        setErr("KAPEI ya estaba revisando el pedido anterior. Pulsa de nuevo para crear un pedido nuevo.");
      } else if (!e.response) {
        setErr("No se ha podido conectar con KAPEI. Comprueba tu conexión y vuelve a intentarlo.");
      } else {
        setErr(formatApiError(detail) || "Error de red. Inténtalo de nuevo.");
      }
    } finally {
      setUploading(false);
    }
  };

  if (success) {
    return (
      <MobileLayout hideBottomNav>
        <div className="mt-6 flex flex-col items-center text-center">
          <CheckCircle2 className="h-20 w-20 text-[#4d7c0f]" strokeWidth={2.5} />
          <h1 data-testid="success-title" className="mt-4 text-3xl font-black text-slate-900">
            {success.created_new_after_closed ? "Nuevo pedido creado" : success.appended ? "Archivos añadidos" : "Pedido enviado correctamente"}
          </h1>
          {success.created_new_after_closed && (
            <div className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 text-sm font-bold text-amber-900">
              KAPEI ya estaba revisando el pedido anterior, así que hemos creado un pedido nuevo con estos archivos.
            </div>
          )}
          <p className="mt-3 text-base text-slate-600">Indica este código en el mostrador:</p>
          <div
            data-testid="success-code"
            className="mt-4 rounded-2xl border-4 border-[#4d7c0f] bg-[#4d7c0f]/5 px-8 py-6 text-5xl font-black tracking-wider text-[#4d7c0f]"
          >
            {success.code}
          </div>
          <p data-testid="success-name" className="mt-4 text-base text-slate-700">
            A nombre de:{" "}
            <strong>
              {success.client_name && success.client_name !== success.code
                ? success.client_name
                : "Invitado"}
            </strong>
          </p>
          <p className="mt-2 text-sm text-slate-500">
            {success.files?.length || 0} archivo(s) · {(success.total_size / 1024).toFixed(0)} KB
            {success.page_count != null && <> · {success.page_count} página(s)</>}
          </p>

          <div className="mt-6 flex w-full items-start gap-3 rounded-2xl border-2 border-dashed border-[#d8b4e2] bg-[#faf5ff] p-4 text-left">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[#7c3aed]" />
            <p className="text-sm text-slate-700">
              Puedes seguir <strong>añadiendo archivos a este pedido</strong> o ver su detalle
              completo mientras KAPEI no lo abra para imprimir.
            </p>
          </div>

          <div className="mt-6 flex w-full flex-col gap-3">
            <Button
              data-testid="success-view-detail"
              onClick={() => navigate(`/codes/${encodeURIComponent(success.code)}`, { replace: true })}
              className="kapei-btn-huge bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
            >
              <Package className="mr-2 h-5 w-5" /> Ver pedido {success.code}
            </Button>
            <Button
              data-testid="success-back-home"
              onClick={() => navigate("/codes", { replace: true })}
              variant="outline"
              className="h-14 rounded-2xl border-2 text-base font-bold"
            >
              Mis códigos
            </Button>
            <Button
              onClick={() => navigate("/", { replace: true })}
              variant="ghost"
              className="h-12 text-sm font-bold text-slate-600"
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout hideBottomNav={fromShare || !!appendMode || uploading}>
      {fromShare ? (
        <button
          onClick={() => {
            if (entries.length > 0) {
              if (!window.confirm("¿Salir sin enviar? Los archivos compartidos se perderán.")) return;
              clearSharedFiles();
              setEntries([]);
            }
            navigate("/", { replace: true });
          }}
          data-testid="send-back"
          className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-slate-600"
        >
          <ArrowLeft className="h-4 w-4" /> Cancelar envío
        </button>
      ) : (
        <button
          onClick={() => navigate("/")}
          data-testid="send-back"
          className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-slate-600"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
      )}

      <div className="mb-2 inline-flex rounded-full border-2 border-slate-200 p-1">
        <button
          data-testid="tab-quick"
          onClick={() => setMode("quick")}
          className={`rounded-full px-4 py-2 text-sm font-bold ${mode === "quick" ? "bg-[#4d7c0f] text-white" : "text-slate-700"}`}
        >
          Envío rápido
        </button>
        <button
          data-testid="tab-configured"
          onClick={() => setMode("configured")}
          className={`rounded-full px-4 py-2 text-sm font-bold ${mode === "configured" ? "bg-[#4d7c0f] text-white" : "text-slate-700"}`}
        >
          Configurar
        </button>
      </div>

      <h1 className="mb-2 mt-2 text-3xl font-black text-slate-900">
        {appendMode ? "Añadir archivos al pedido" : mode === "quick" ? "Envío rápido" : "Configurar impresión"}
      </h1>

      {fromShare && (
        <div
          data-testid="from-share-banner"
          className={`mb-4 rounded-2xl border-2 p-3 text-sm ${
            entries.length > 0
              ? "border-[#4d7c0f]/30 bg-[#4d7c0f]/5 text-[#4d7c0f]"
              : "border-amber-400 bg-amber-50 text-amber-900"
          }`}
        >
          {entries.length > 0 ? (
            <>
              <div className="font-bold">
                {entries.length} archivo{entries.length === 1 ? "" : "s"} recibido{entries.length === 1 ? "" : "s"}
              </div>
              <div className="mt-1 text-xs">
                Pulsa <b>Enviar a KAPEI</b> abajo para obtener tu código KP.
              </div>
            </>
          ) : (
            <div className="text-xs">
              No se han recibido archivos. Vuelve a la app de origen y pulsa{" "}
              <b>Compartir → Enviar a KAPEI</b> de nuevo.
            </div>
          )}
        </div>
      )}

      {fromShare && shareErrors.length > 0 && isShareDebugEnabled() && (
        <div
          data-testid="share-errors"
          className="mb-4 rounded-2xl border-2 border-red-400 bg-red-50 p-3 text-xs text-red-900"
        >
          <div className="mb-1 font-bold uppercase tracking-widest">
            Errores al leer archivos compartidos
          </div>
          <ul className="space-y-1 font-mono">
            {shareErrors.map((er, i) => (
              <li key={i}>• {er}</li>
            ))}
          </ul>
        </div>
      )}

      {fromShare && isShareDebugEnabled() && (
        <div className="mb-4">
          <button
            type="button"
            data-testid="show-share-debug"
            onClick={() => {
              setDebugLog(getShareDebugLog());
              setShowDebug((v) => !v);
            }}
            className="text-xs font-bold uppercase tracking-wider text-slate-500 underline"
          >
            {showDebug ? "Ocultar" : "Ver"} diagnóstico técnico
          </button>
          {showDebug && (
            <pre
              data-testid="share-debug-log"
              className="mt-2 max-h-72 overflow-auto rounded-xl bg-slate-900 p-3 text-[10px] leading-tight text-green-300"
            >
              {debugLog.length === 0
                ? "(sin entradas — no se ha recibido ningún share intent)"
                : debugLog.join("\n")}
            </pre>
          )}
        </div>
      )}

      {appendMode && openOrder && (
        <div data-testid="append-banner" className="mb-4 rounded-2xl border-2 border-[#4d7c0f]/30 bg-[#4d7c0f]/5 p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-[#4d7c0f]">Añadiendo archivos a</div>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            <span className="font-mono text-2xl font-black tracking-wider text-[#4d7c0f]">{openOrder.code}</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="append-view-detail"
                onClick={() => navigate(`/codes/${encodeURIComponent(openOrder.code)}`)}
                className="rounded-full border-2 border-[#4d7c0f] px-3 py-1 text-xs font-bold text-[#4d7c0f]"
              >
                Ver detalle del pedido
              </button>
            </div>
          </div>
          <div className="mt-2 text-xs text-slate-600">
            Añade archivos y, si quieres, indica nuevas preferencias para esta tanda. KAPEI verá la última preferencia y el historial de envíos.
          </div>
        </div>
      )}

      {activePrize && (
        <div className="mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="text-xs font-black uppercase tracking-widest">
            🎁 Este pedido usará tu premio
          </div>
          <div className="mt-1 text-lg font-black text-slate-900">
            {activePrize.title || activePrize.code}
          </div>
          {activePrize.gift && (
            <div className="mt-1">
              KAPEI aplicará o entregará: <strong>{activePrize.gift}</strong>
            </div>
          )}
          <div className="mt-2 text-xs font-semibold">
            Este premio se canjeará completo en este pedido. Si el pedido es inferior al valor del premio, la parte no utilizada se perderá.
          </div>
          <button
            type="button"
            onClick={deactivateActivePrize}
            className="mt-3 rounded-xl border border-amber-300 bg-white px-4 py-2 text-xs font-black text-amber-900"
          >
            No usar este premio ahora
          </button>
        </div>
      )}

      {mode === "configured" && (
        <ServiceSelector
          services={configuratorServices}
          selectedSlug={selectedService?.slug}
          onSelect={setSelectedService}
        />
      )}

      <button
        type="button"
        data-testid="file-drop"
        onClick={() => inputRef.current?.click()}
        className="w-full cursor-pointer rounded-3xl border-4 border-dashed border-[#4d7c0f]/40 bg-[#4d7c0f]/5 p-8 text-center transition-colors hover:bg-[#4d7c0f]/10"
      >
        <Upload className="mx-auto h-10 w-10 text-[#4d7c0f]" strokeWidth={2.5} />
        <p className="mt-3 text-xl font-extrabold text-slate-900">Seleccionar archivos</p>
        <p className="text-sm text-slate-600">PDF, fotos, documentos…</p>
        <input
          data-testid="file-input"
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => onPick(e.target.files)}
        />
      </button>

      {entries.length > 0 && (
        <ul data-testid="file-list" className="mt-4 flex flex-col gap-3">
          {entries.map((entry, i) => (
            <FileCard
              key={`${entry.file.name}-${entry.file.size}-${entry.file.lastModified}-${i}`}
              entry={entry}
              index={i}
              opts={opts}
              onDelete={removeFile}
            />
          ))}
          <li className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
            <span className="flex items-center gap-1 font-bold text-slate-700">
              <Files className="h-3.5 w-3.5" />
              {!hasUnknownPageCount
                ? <>Total: <strong className="text-[#4d7c0f]">{totalPages}</strong> página(s)</>
                : <>Total páginas: <strong>—</strong> (algún archivo no se pudo contar)</>}
            </span>
            <span className="text-slate-500">{(totalSize / 1024).toFixed(0)} KB</span>
          </li>
        </ul>
      )}

      <div className="mt-6 flex flex-col gap-4">
          {!appendMode && (
          <div>
            <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">
              {user ? "A nombre de" : "¿A nombre de quién lo dejamos?"}
            </Label>
            <Input
              data-testid="name-input"
              value={nameOverride}
              onChange={(e) => setNameOverride(e.target.value)}
              placeholder={user ? "Tu nombre" : "Tu nombre o deja el código"}
              className="mt-2 h-14 rounded-xl border-2 border-slate-300 px-4 text-lg"
            />
            {!user && (
              <p className="mt-1 text-xs text-slate-500">
                Si lo dejas con el código, KAPEI te identificará por <strong>{codeHint}</strong>.
              </p>
            )}
          </div>
          )}

          <div>
            <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">Nota (opcional)</Label>
            <Textarea
              data-testid="note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: Imprimir solo páginas 1-5, grapado…"
              className="mt-2 min-h-[100px] rounded-xl border-2 border-slate-300 p-4 text-base"
            />
          </div>

          {mode === "configured" && (
            <DynamicConfigurator
              opts={opts}
              setOpts={setOpts}
              sizeOther={sizeOther}
              setSizeOther={setSizeOther}
              paperOther={paperOther}
              setPaperOther={setPaperOther}
              catalog={serviceCatalog}
              service={selectedService}
              entries={entries}
            />
          )}

          {user?.is_reliable && user?.allow_pickup_ready && (
            <div className="rounded-2xl border-2 border-[#d8b4e2] bg-[#faf5ff] p-4">
              <div className="text-sm font-bold uppercase tracking-widest text-[#7c3aed]">
                Si es posible, me gustaría que lo imprimáis…
              </div>
              <RadioGroup
                value={pickup ? "before" : "anytime"}
                onValueChange={(v) => {
                  const wantsPickup = v === "before";
                  setPickup(wantsPickup);
                  if (wantsPickup) ensurePickupDateTime();
                }}
                className="mt-3 flex flex-col gap-2"
              >
                <label
                  data-testid="pickup-anytime"
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3 text-base font-bold ${
                    !pickup ? "border-[#7c3aed] bg-white text-[#7c3aed]" : "border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  <RadioGroupItem value="anytime" className="sr-only" />
                  Cuando pase por el local
                </label>
                <label
                  data-testid="pickup-before"
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3 text-base font-bold ${
                    pickup ? "border-[#7c3aed] bg-white text-[#7c3aed]" : "border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  <RadioGroupItem value="before" className="sr-only" />
                  A poder ser, que esté impreso antes de…
                </label>
              </RadioGroup>
              {pickup && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-500">
                      Día laborable
                    </label>

                    <div className="grid grid-cols-2 gap-2">
                      {visiblePickupDays.map((d) => (
                        <button
                          key={d.value}
                          type="button"
                          data-testid={`pickup-day-${d.value}`}
                          onClick={() => updatePickupDateTime(d.value, pickupTime)}
                          className={`rounded-xl border-2 p-3 text-left text-sm font-black ${
                            pickupDate === d.value
                              ? "border-[#7c3aed] bg-white text-[#7c3aed]"
                              : "border-slate-200 bg-white text-slate-700"
                          }`}
                        >
                          {d.label}
                          <span className="mt-1 block text-xs font-semibold text-slate-500">
                            {new Date(`${d.value}T12:00:00`).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}
                          </span>
                        </button>
                      ))}
                    </div>

                    {extraPickupDays.length > 0 && (
                      <select
                        data-testid="pickup-extra-day"
                        value={extraPickupDays.some((d) => d.value === pickupDate) ? pickupDate : ""}
                        onChange={(e) => {
                          if (e.target.value) updatePickupDateTime(e.target.value, pickupTime);
                        }}
                        className="mt-3 h-14 w-full rounded-xl border-2 border-slate-300 bg-white px-4 text-base font-bold"
                      >
                        <option value="">Otra fecha laborable…</option>
                        {extraPickupDays.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label} · {new Date(`${d.value}T12:00:00`).toLocaleDateString("es-ES")}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">
                      Hora aproximada
                    </label>

                    <select
                      data-testid="pickup-time"
                      value={pickupTime}
                      onChange={(e) => updatePickupDateTime(pickupDate || pickupDays[0]?.value, e.target.value)}
                      className="h-14 w-full rounded-xl border-2 border-slate-300 bg-white px-4 text-base font-bold"
                    >
                      <option value="">Elige una hora</option>
                      {pickupTimeSlots.map((slot) => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <p className="mt-2 text-xs text-slate-600">
                Es solo una preferencia de recogida. En algunos trabajos, KAPEI puede solicitar una señal antes de preparar el pedido.
              </p>
            </div>
          )}
        </div>

      {err && <div data-testid="send-error" className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{err}</div>}

      {uploading && (
        <div className="mt-6 rounded-2xl border-2 border-slate-200 bg-white p-4" data-testid="upload-progress">
          <p className="mb-2 text-base font-bold text-slate-900">Subiendo archivos… {progress}%</p>
          <Progress value={progress} className="h-3 [&>div]:bg-[#4d7c0f]" />
          {progress < 100 && (
            <p className="mt-2 text-xs text-slate-500">No cierres la app hasta que termine el envío.</p>
          )}
        </div>
      )}

      <Button
        data-testid="submit-order"
        disabled={uploading || entries.length === 0}
        onClick={tryStartUpload}
        className="kapei-btn-huge mt-6 bg-[#4d7c0f] text-white hover:bg-[#3f6212] disabled:opacity-50"
      >
        {uploading
          ? `Enviando… ${progress}%`
          : appendMode
          ? `Añadir al pedido ${openOrder?.code || ""}`
          : "Enviar a KAPEI"}
      </Button>
      <p className="mt-3 text-center text-xs text-slate-500">Normalmente pagarás al recoger. Algunos trabajos pueden requerir una señal o pago online.</p>

      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle data-testid="open-order-title" className="text-2xl font-black">Ya tienes un pedido abierto</DialogTitle>
          </DialogHeader>
          <div className="rounded-xl border-2 border-[#4d7c0f]/30 bg-[#4d7c0f]/5 p-4 text-center">
            <span data-testid="open-order-code" className="font-mono text-3xl font-black tracking-wider text-[#4d7c0f]">
              {openOrder?.code}
            </span>
            <p className="mt-2 text-xs text-slate-600">{openOrder?.files?.length || 0} archivo(s) ya enviados</p>
          </div>
          <p className="text-sm text-slate-700">¿Qué quieres hacer con estos archivos?</p>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              data-testid="open-order-append"
              onClick={() => { const id = openOrder.id; setAppendMode(id); setShowOpenDialog(false); submit(false, id); }}
              className="kapei-btn-huge w-full bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
            >
              <Plus className="mr-2 h-5 w-5" /> Añadir a {openOrder?.code}
            </Button>
            <Button
              data-testid="open-order-new"
              onClick={() => { setAppendMode(null); setOpenOrder(null); setShowOpenDialog(false); submit(true); }}
              variant="outline"
              className="h-14 w-full rounded-2xl border-2 text-base font-bold"
            >
              Crear pedido nuevo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}
